import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getServerError, apiErrorsToFormik } from 'utils/errors';

import Alert from 'sharedComponents/Alert';
import Seo from 'sharedComponents/Seo';
import { Typography, Button } from '@material-ui/core';

import useApp from 'reducers/hooks/useApp';
import useEmailConfirmation from 'reducers/hooks/auth/useEmailConfirmation';

import UserPresenter from 'presenters/UserPresenter';

import UpdatePasswordForm from './components/UpdatePasswordForm';
import ProfileForm from './components/ProfileForm';

import useStyles from './useStyles';

const RealEstateAgentSettingsPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    updatePassword,
    currentUser,
    updateRealEstateAgent,
    saveCompanyLogo,
    saveProfileImage,
    isProfileCompanyLogoSaving,
    isProfileImageSaving,
  } = useApp();

  const { resendEmailConfirmation, isEmailConfirmationResending } = useEmailConfirmation();

  const [alert, setAlert] = useState({ show: false, text: '', severity: '' });
  const isConfirmed = UserPresenter.emailConfirmed(currentUser);

  const handleSubmitUpdatePassword = async (values, { resetForm, setErrors }) => {
    try {
      await updatePassword(values);
      resetForm();
      setAlert({
        show: true,
        text: t('components.RealEstateAgentSettingsPage.passwordUpdated'),
        severity: 'success',
      });
    } catch (error) {
      const { fullMessages } = apiErrorsToFormik(error);
      setErrors({ currentPassword: fullMessages });
    }
  };

  const handleResetEmailConfirmation = async () => {
    try {
      await resendEmailConfirmation();
      setAlert({
        show: true,
        text: t('components.RealEstateAgentSettingsPage.emailSuccessfullySent'),
        severity: 'success',
      });
    } catch (error) {
      const message = getServerError(error);
      setAlert({ show: true, text: message, severity: 'error' });
    }
  };

  const handleSubmitProfileForm = async values => {
    try {
      await updateRealEstateAgent(values);
      setAlert({
        show: true,
        text: t('components.RealEstateAgentSettingsPage.changesSaved'),
        severity: 'success',
      });
    } catch (error) {
      const message = getServerError(error);
      setAlert({ show: true, text: message, severity: 'error' });
    }
  };

  const handleSaveProfileImage = async profileImage => {
    const attr = { profileImage };
    await saveProfileImage(attr);
  };

  const handleSaveCompanyLogo = async companyLogoImage => {
    const attr = { companyLogoImage };
    await saveCompanyLogo(attr);
  };

  const handleCloseAlert = () => setAlert({ ...alert, show: false });

  return (
    <div className={classes.container}>
      <Seo
        title={t('meta.RealEstateAgentSettingsPage.title')}
        description={t('meta.RealEstateAgentSettingsPage.description')}
      />
      <div className={classes.content}>
        <Typography variant="h1" className={classes.header}>
          {t('components.RealEstateAgentSettingsPage.header')}
        </Typography>
        {!isConfirmed && (
          <div className={classes.confirmation}>
            <Typography
              variant="body1"
              dangerouslySetInnerHTML={{
                __html: t('components.RealEstateAgentSettingsPage.needConfirmation', {
                  email: UserPresenter.email(currentUser),
                }),
              }}
            />
            <Button
              onClick={handleResetEmailConfirmation}
              className={classes.resendConfirmationButton}
              disabled={isEmailConfirmationResending}
            >
              {t('components.RealEstateAgentSettingsPage.resendConfirmationButton')}
            </Button>
          </div>
        )}
        <div className={classes.forms}>
          <div className={classes.form}>
            <ProfileForm
              onSubmit={handleSubmitProfileForm}
              currentUser={currentUser}
              isProfileImageSaving={isProfileImageSaving}
              saveProfileImage={handleSaveProfileImage}
              isCompanyLogoSaving={isProfileCompanyLogoSaving}
              saveCompanyLogo={handleSaveCompanyLogo}
            />
          </div>
          <div className={classes.form}>
            <UpdatePasswordForm onSubmit={handleSubmitUpdatePassword} />
          </div>
        </div>
        {alert.show && <Alert text={alert.text} open severity={alert.severity} handleClose={handleCloseAlert} />}
      </div>
    </div>
  );
};

export default RealEstateAgentSettingsPage;



// WEBPACK FOOTER //
// ./src/components/RealEstateAgentSettingsPage/RealEstateAgentSettingsPage.js